import { useCallback, useEffect, useMemo, useRef } from 'react';
import globalWindow from 'global';
import type { OnChangeStageSettings, OnCommitStageSettings } from '../types';
const DEFAULT_MIN_SCALE = 0.2;
const DEFAULT_MAX_SCALE = 50;
const DEFAULT_ZOOM_FACTOR = 1.1;
const ZOOM_FLUSH_DELAY_MS = 150;
type ZoomAndPanParamsT = {
  containerRef: React.RefObject<HTMLDivElement>;
  stageRef: React.RefObject<HTMLDivElement>;
  isDraggable: boolean;
  suppressPan: boolean;
  onChangeStageSettings: OnChangeStageSettings;
  onCommitStageSettings: OnCommitStageSettings;
  onPanBlocked?: () => void;
  onZoom?: (scale: number) => void;
  minScale?: number;
  maxScale?: number;
  zoomFactor?: number;
};
export function useZoomAndPan({
  containerRef,
  stageRef,
  isDraggable,
  suppressPan,
  onChangeStageSettings,
  onCommitStageSettings,
  onPanBlocked,
  onZoom,
  minScale = DEFAULT_MIN_SCALE,
  maxScale: maxScaleProp,
  zoomFactor = DEFAULT_ZOOM_FACTOR
}: ZoomAndPanParamsT) {
  const isMousePressedRef = useRef(false);
  const zoomFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggableRef = useRef(isDraggable);
  // eslint-disable-next-line react-hooks/refs
  isDraggableRef.current = isDraggable;
  const suppressPanRef = useRef(suppressPan);
  // eslint-disable-next-line react-hooks/refs
  suppressPanRef.current = suppressPan;
  const onPanBlockedRef = useRef(onPanBlocked);
  // eslint-disable-next-line react-hooks/refs
  onPanBlockedRef.current = onPanBlocked;
  const onZoomRef = useRef(onZoom);
  // eslint-disable-next-line react-hooks/refs
  onZoomRef.current = onZoom;
  const maxScale = useMemo(() => {
    if (maxScaleProp !== undefined) return maxScaleProp;
    // eslint-disable-next-line react-hooks/refs
    if (!stageRef.current) return DEFAULT_MAX_SCALE;
    const deviceResolution = globalWindow.screen.width * globalWindow.devicePixelRatio;
    // eslint-disable-next-line react-hooks/refs
    return deviceResolution / stageRef.current.clientWidth * 100;
  }, [stageRef, maxScaleProp]);
  const handleZoom = useCallback((originX: number, originY: number, scaleFactor: number) => {
    onChangeStageSettings(prev => {
      const isZoomingIn = scaleFactor > 1;
      if (prev.scale <= minScale && !isZoomingIn) return prev;
      if (prev.scale >= maxScale && isZoomingIn) return prev;
      const nextScale = Math.max(Math.min(prev.scale * scaleFactor, maxScale), minScale);
      if (Math.floor(prev.scale) !== Math.floor(nextScale)) {
        onZoomRef.current?.(nextScale);
      }
      return {
        ...prev,
        scale: nextScale,
        x: originX - (originX - prev.x) * scaleFactor,
        y: originY - (originY - prev.y) * scaleFactor
      };
    });
  }, [maxScale, minScale, onChangeStageSettings]);
  const handleMouseDown = useCallback(() => {
    isMousePressedRef.current = true;
  }, []);
  const handleMouseUp = useCallback(() => {
    isMousePressedRef.current = false;
    onCommitStageSettings();
  }, [onCommitStageSettings]);
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isMousePressedRef.current || suppressPanRef.current) return;
    const target = e.target && 'tagName' in e.target ? (e.target as HTMLElement) : null;
    const isStage = target?.tagName === 'svg' && target?.dataset?.['stageSvg'] === 'true';
    if (!isStage) return;
    if (!isDraggableRef.current && !e.altKey) {
      onPanBlockedRef.current?.();
      return;
    }
    e.preventDefault();
    onChangeStageSettings(prev => ({
      ...prev,
      x: prev.x + e.movementX,
      y: prev.y + e.movementY
    }));
  }, [onChangeStageSettings]);
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!stageRef.current) return;
    e.preventDefault();
    const {
      clientWidth: stageW,
      clientHeight: stageH
    } = stageRef.current;
    handleZoom(e.pageX - stageW / 2, e.pageY - stageH / 2, e.deltaY < 0 ? zoomFactor : 1 / zoomFactor);

    // Wheel has no mouseup equivalent — commit after a short idle period
    if (zoomFlushTimerRef.current) clearTimeout(zoomFlushTimerRef.current);
    zoomFlushTimerRef.current = setTimeout(() => {
      onCommitStageSettings();
      zoomFlushTimerRef.current = null;
    }, ZOOM_FLUSH_DELAY_MS);
  }, [stageRef, handleZoom, onCommitStageSettings, zoomFactor]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('wheel', handleWheel);
    };
  }, [containerRef, handleMouseDown, handleMouseUp, handleMouseMove, handleWheel]);
}